import { useEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useProject } from '@/hooks/useProject';
import { useProjects } from '@/hooks/useProjects';

/**
 * Layout wrapper for all /projects/:id/* routes.
 * Syncs the URL :id param to the active project in context.
 * If the URL id is invalid, redirects to the first available project.
 */
export function ProjectLayout() {
  const { id } = useParams<{ id: string }>();
  const { activeProject, setActiveProject, loading: projectLoading } = useProject();
  const { projects, loading: projectsLoading } = useProjects();
  const navigate = useNavigate();

  const loading = projectLoading || projectsLoading;

  useEffect(() => {
    if (loading || !id) return;

    const urlProject = projects.find((p) => p.id === id);

    // Archived projects are read-only — bounce to /projects so the user can
    // restore from the Archived section. RLS still returns these to the owner
    // for the 30-day unarchive window, so we need a UI-level guard.
    if (urlProject?.archived_at) {
      toast.error('This project is archived. Restore it to continue.');
      navigate('/projects', { replace: true });
      return;
    }

    if (projects.length === 0) return;

    // URL project matches active — nothing to do
    if (activeProject?.id === id) return;

    if (urlProject) {
      // Valid project in URL — sync context to match
      setActiveProject(urlProject);
    } else {
      // Invalid project ID — redirect to first non-archived project, or /projects
      const firstActive = projects.find((p) => p.archived_at === null);
      if (firstActive) {
        navigate(`/projects/${firstActive.id}`, { replace: true });
      } else {
        navigate('/projects', { replace: true });
      }
    }
  }, [id, projects, loading, activeProject?.id, setActiveProject, navigate]);

  return <Outlet />;
}
